import { useRef, useState } from "react";
import { useIsOnline } from "../../../hooks/index.ts";
import type { LibraryGame } from "./adapter.ts";
import { WeekPicker } from "./WeekPicker.tsx";
import { normalizeWeekName } from "./weeks.ts";

type Props = {
  game: LibraryGame;
  /** The team's weeks (libraryWeekNames of the library's games). */
  weeks: readonly string[];
  /**
   * Files the game under a week (null = none); resolves true once the server
   * has it. Omit to keep the week read-only.
   */
  onSave?: (gameId: number, weekName: string | null) => Promise<boolean>;
};

/**
 * A game's practice week in the Game Library (client 2026-10-06): tapping it
 * opens the team's weeks (the design kit's picker), with "+ New week" at the
 * end to type a new one.
 *
 * A pick shows at once and saves straight away (a dropdown pick is whole —
 * nothing half-typed to wait for); a failed save puts the saved week back.
 * The page sends one game's picks to the server in the order they were made
 * (even across a redraw of this row), so the last one is the one that
 * sticks. The current (in-progress) game's week is picked when it is saved,
 * so it stays plain text — as does everything offline.
 */
export function GameWeekCell({ game, weeks, onSave }: Props) {
  const { isOnline } = useIsOnline();
  const gameId = game.game_id;
  const saved = normalizeWeekName(game.week_name);
  // What the server has once a save lands, until the list catches up — a
  // list reload that fails must not put the old week back, nor must a later
  // pick that fails (as GameDateCell's `confirmed`).
  const [confirmed, setConfirmed] = useState<{ week: string | null } | null>(null);
  if (confirmed && confirmed.week === saved) setConfirmed(null);
  // The newest pick, on screen while it saves.
  const [pick, setPick] = useState<{ week: string | null } | null>(null);
  const latest = useRef(0);

  const shown = pick ? pick.week : confirmed ? confirmed.week : saved;
  const editable =
    Boolean(onSave) && Boolean(game.complete) && gameId != null && isOnline;
  if (!editable || !onSave || gameId == null) return <>{shown ?? ""}</>;

  const choose = (week: string | null) => {
    if (week === shown) return;
    const mine = ++latest.current;
    setPick({ week });
    void (async () => {
      let ok = false;
      try {
        ok = await onSave(gameId, week);
      } catch {
        // The page says what went wrong.
      }
      // The page sends one game's picks in order, so each one that lands is
      // the newest the server has.
      if (ok) setConfirmed({ week });
      // A newer pick is on screen: its own save settles it.
      if (mine === latest.current) setPick(null);
    })();
  };

  return (
    <WeekPicker
      variant="cell"
      value={shown}
      weeks={weeks}
      onChange={choose}
      label={`Week of ${game.game_description || "game"}`}
    />
  );
}
